import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    Grid,
    Box,
    Typography,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormHelperText,
    Alert,
} from '@mui/material';
import * as Yup from 'yup';
import { useFormikContext } from 'formik';
import InputField from './form-fields/InputField';
import WebsiteForms from './WebsiteForms';

const PSGConferenceFormRoot = styled('div')(({ theme }) => ({
    padding: '50px 0',
}));

const formModel = {
    name: {
        name: 'name',
        label: 'Name*',
        requiredErrorMsg: 'Name is required',
    },
    email: {
        name: 'email',
        label: 'Email*',
        requiredErrorMsg: 'Email is required',
    },
    contactNumber: {
        name: 'contactNumber',
        label: 'Contact Number*',
        requiredErrorMsg: 'Contact number is required',
    },
    branch: {
        name: 'branch',
        label: 'Branch/Franchise*',
        requiredErrorMsg: 'Branch/Franchise is required',
    },
    hasAgency: {
        name: 'hasAgency',
        label: 'Do you have an agency with CIA?*',
        requiredErrorMsg: 'This field is required',
    },
    hasSignedUpPortal: {
        name: 'hasSignedUpPortal',
        label: 'Have you signed up to the CIA Ninja Broker Portal?*',
        requiredErrorMsg: 'This field is required',
    },
};

const { name, email, contactNumber, branch, hasAgency, hasSignedUpPortal } = formModel;

const initialValues = {
    [name.name]: '',
    [email.name]: '',
    [contactNumber.name]: '',
    [branch.name]: '',
    [hasAgency.name]: '',
    [hasSignedUpPortal.name]: '',
};

const validationSchema = Yup.object().shape({
    [name.name]: Yup.string().required(`${name.requiredErrorMsg}`),
    [email.name]: Yup.string()
        .email('Incorrect email format')
        .required(`${email.requiredErrorMsg}`),
    [contactNumber.name]: Yup.string().required(`${contactNumber.requiredErrorMsg}`),
    [branch.name]: Yup.string().required(`${branch.requiredErrorMsg}`),
    [hasAgency.name]: Yup.string().required(`${hasAgency.requiredErrorMsg}`),
    [hasSignedUpPortal.name]: Yup.string().required(`${hasSignedUpPortal.requiredErrorMsg}`),
});

// Custom RadioGroupField component to handle errors
const RadioGroupField = ({ name, label, options }) => {
    const { errors, touched, values, handleChange } = useFormikContext();
    const hasError = touched[name] && Boolean(errors[name]);

    return (
        <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }} error={hasError}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                }}
            >
                <FormLabel component="legend" focused={false}>
                    {label}
                </FormLabel>
                <RadioGroup
                    aria-label={name}
                    name={name}
                    value={values[name] || ''}
                    onChange={handleChange}
                    row
                    sx={{ mt: { xs: 1, sm: 0 } }}
                >
                    {options.map((option) => (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio />}
                            label={option.label}
                        />
                    ))}
                </RadioGroup>
            </Box>
            {hasError && <FormHelperText>{errors[name]}</FormHelperText>}
        </FormControl>
    );
};

const PSGConferenceForm = (props) => {
    const { source } = props;
    const { name, email, contactNumber, branch, hasAgency, hasSignedUpPortal } = formModel;

    const [response, setResponse] = useState(undefined);

    if (response && response.isSuccessful) {
        return (
            <PSGConferenceFormRoot>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        padding: '0px 30px',
                    }}
                >
                    <Alert severity="success">Successfully submitted your request!</Alert>

                    <Typography
                        variant="subtitle1"
                        maxWidth={1000}
                        textAlign="center"
                        marginTop={3}
                    >
                        Thank you for submitting your details to win a Xiaomi Robo Vacuum S20. A
                        draw will be held daily at 16:00, winners will be notified via the contact
                        details provided.
                    </Typography>
                </Box>
            </PSGConferenceFormRoot>
        );
    }

    return (
        <PSGConferenceFormRoot>
            <WebsiteForms
                formUrl="api/forms/prizes/psg-conference"
                initialValues={{
                    ...initialValues,
                    source: source || '',
                }}
                validationSchema={validationSchema}
                handleResponse={(response) => setResponse(response)}
            >
                <Grid container spacing={7}>
                    <Grid item xs={12} sm={6}>
                        <InputField name={name.name} label={name.label} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputField name={email.name} label={email.label} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputField
                            name={contactNumber.name}
                            label={contactNumber.label}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputField name={branch.name} label={branch.label} fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                        <RadioGroupField
                            name={hasAgency.name}
                            label={hasAgency.label}
                            options={[
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' },
                            ]}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <RadioGroupField
                            name={hasSignedUpPortal.name}
                            label={hasSignedUpPortal.label}
                            options={[
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' },
                            ]}
                        />
                    </Grid>
                </Grid>
            </WebsiteForms>
        </PSGConferenceFormRoot>
    );
};

export default PSGConferenceForm;
